import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type PollingStatus = 'idle' | 'polling' | 'completed' | 'viewed'

export interface PollingEntry {
  hashKey: string
  status: PollingStatus
  startedAt: number
  /** 사주 유형 (라우트 path variable) */
  sajuType: string
  /** 분석 대상자 이름 */
  subjectName: string
  /** 사주 분석 이름 (예: 정통사주) */
  sajuName: string
}

interface PollingState {
  entries: Record<string, PollingEntry>
}

interface PollingGetters {
  getEntry: (hashKey: string) => PollingEntry | undefined
  getAllEntries: () => Record<string, PollingEntry>
}

interface PollingActions {
  setPolling: (
    hashKey: string,
    options: { sajuType: string; subjectName: string; sajuName: string },
  ) => void
  setCompleted: (hashKey: string) => void
  setViewed: (hashKey: string) => void
  remove: (hashKey: string) => void
  reset: () => void
}

type PollingStore = PollingState & PollingGetters & PollingActions

const INITIAL_STATE: PollingState = {
  entries: {},
}

/** entries에서 우선순위(polling > completed)에 따라 active entry를 resolve한다. selector로 사용. */
export function selectActiveEntry(entries: Record<string, PollingEntry>): PollingEntry | null {
  const priorities: PollingStatus[] = ['polling', 'completed']
  for (const priority of priorities) {
    const found = Object.values(entries).find((entry) => entry.status === priority)
    if (found) {
      return found
    }
  }
  return null
}

export const usePollingStore = create<PollingStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setPolling: (hashKey, options) => {
        set((state) => ({
          entries: {
            ...state.entries,
            [hashKey]: {
              hashKey,
              status: 'polling' as const,
              startedAt: Date.now(),
              sajuType: options?.sajuType,
              subjectName: options?.subjectName,
              sajuName: options?.sajuName,
            },
          },
        }))
      },

      setCompleted: (hashKey) => {
        set((state) => {
          const entry = state.entries[hashKey]
          if (!entry) {
            return state
          }
          return {
            entries: {
              ...state.entries,
              [hashKey]: { ...entry, status: 'completed' as const },
            },
          }
        })
      },

      setViewed: (hashKey) => {
        set((state) => {
          const entry = state.entries[hashKey]
          if (!entry) {
            return state
          }
          return {
            entries: {
              ...state.entries,
              [hashKey]: { ...entry, status: 'viewed' as const },
            },
          }
        })
      },

      remove: (hashKey) => {
        set((state) => {
          const { [hashKey]: _, ...rest } = state.entries
          return { entries: rest }
        })
      },

      reset: () => {
        set({ ...INITIAL_STATE })
      },

      getEntry: (hashKey) => get().entries[hashKey],
      getAllEntries: () => get().entries,
    }),
    {
      name: 'saju-polling',
      partialize: (state) => ({
        entries: state.entries,
      }),
      merge: (persisted, current) => {
        const restored = persisted as Partial<PollingState> | undefined
        if (!restored?.entries) {
          return current
        }
        return {
          ...current,
          entries: restored.entries,
        }
      },
    },
  ),
)
